import { Card, Checkbox, Chip, CircularProgress, Divider, capitalize, makeStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import styles from 'assets/jss/material-dashboard-pro-react/views/services/MicroBundle/additionalBenefitsStyle';
import { BodyMBold, BodyS, BodySBold, DescriptionM, HeadingLBold } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import idConfig from 'utils/ecommerce/services/id.config';
import { env } from 'config/app.config';
import classNames from 'classnames';
import ServiceInfo from '../commons/ServiceInfo';
import InfoClause from 'components/infoClause/infoClause';
import { ARBOR_E_FORM_ID, MK_365_PART_NUMBERS, MK_SMS_PART_NUMBERS, SERVICE_STATUS, WEB_PLAN_INFO } from '../constants';
import { isServiceActive, isServiveActiveOrPending } from 'utils/helpers';
import { getLanguage } from 'utils/language/language';
import { CustomLabel } from '../commons/CustomLabel';
import { useSelector } from 'react-redux';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import {
	getAamaliPlusLabels,
	getPlatformExternalLinks,
	getTutorialExternalLinks,
} from 'redux/modules/dynamicLabels/selectors';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import Button from 'components/CustomButtons/Button';
import Note from 'views/Numbers/Details/Services/subcomponents/common/Note';
import { SingleCommitmentDetails } from 'utils/api/models/payments/singleCommitmentDetails';
import { isms365Enabled } from 'utils/killswitch';

interface IMBPlans {
	selectedOption?: types.ecommerce.SelectedPlan;
	checkedState: boolean[];
	setCheckedState: Function;
	productInfo: any[];
	setProductInfo?: Function;
	newCustomer: boolean;
	planName?: string;
	isServiceLoading?: boolean;
	activeCommitment?: SingleCommitmentDetails | undefined;
}
const useStyles = makeStyles(styles);
export const AdditionalBenefits = ({
	selectedOption,
	checkedState,
	setCheckedState,
	productInfo,
	setProductInfo,
	newCustomer,
	planName,
	isServiceLoading,
	activeCommitment,
}: IMBPlans) => {
	const useStyles = makeStyles(styles);
	const classes = useStyles();
	const [loading, setLoading] = useState<boolean>(false);
	const [selectAll, setSelectAll] = useState<boolean>(false);
	const language = getLanguage();
	const aamaliPlusLabels = useSelector(getAamaliPlusLabels);
	const platformExtLink = useSelector(getPlatformExternalLinks);
	const tutorialExtLink = useSelector(getTutorialExternalLinks);
	const remoteConfig = useSelector(remoteConfigSelector);
	const enableMs365 = isms365Enabled(remoteConfig);
	const extractServiceData = (attributes: any) => {
		const filteredAttribute = attributes?.filter((attribute: any) => attribute?.identifier === WEB_PLAN_INFO);
		const stringifiedAttributes = filteredAttribute?.[0]?.values?.[0]?.value || '{}';
		return stringifiedAttributes;
	};
	useEffect(() => {
		if (newCustomer && selectedOption) {
			setLoading(true);
			ECommerceService.getContentByPartNumber(selectedOption?.partNumber).subscribe(({ response }: any) => {
				setLoading(false);
				const arbor_form_ids = response?.catalogEntryView[0]?.attributes?.filter(
					(el: any) => el.identifier == ARBOR_E_FORM_ID
				);
				const formIds = arbor_form_ids.map((id: any) => id?.values[0].value);
				ECommerceService.getGroupContent(idConfig.MICROBUNDLE.PRODUCT_INFO[env]).subscribe(
					({ response }: any) => {
						setLoading(false);
						const resp = response?.catalogEntryView;
						const filteredResponse = formIds.map(
							(id: string) =>
								resp?.filter((service: types.ecommerce.CatalogEntryView) => {
									return service.partNumber === id;
								})?.[0]
						);
						if (setProductInfo) setProductInfo(filteredResponse);
					}
				);
			});
		}
	}, [language]);

	useEffect(() => {
		setCheckedState(new Array(productInfo.length).fill(false));
	}, [productInfo]);

	useEffect(() => {
		//set select all to true only when all the items value if checkedState is true
		if (checkedState.length > 0 && checkedState.every((state) => state)) {
			setSelectAll(true);
		} else setSelectAll(false);
	}, [checkedState]);

	const getCheckedStatus = (checkedState: boolean, status: string) => {
		if (isServiveActiveOrPending(status)) return !!true;
		else return !!checkedState;
	};

	// used to select/unselect all services when user clicks on selectAll
	const selectAllOnChange = () => {
		const newSelectAllState = !selectAll;
		setSelectAll(!selectAll);
		if (!!activeCommitment) {
			if (newSelectAllState) {
				// const updatedCheckedState = checkedState.map((state, index) => (index === mk365Index ? false : true));
				const updatedCheckedState = checkedState.map(() => true);
				console.log(updatedCheckedState);
				setCheckedState(updatedCheckedState);
			} else {
				const updatedCheckedState = checkedState.map(() => false);
				setCheckedState(updatedCheckedState);
			}
		}
	};

	const handleSingleCheckbox = (i: number) => {
		const updatedState = checkedState.map((state, index) => (index == i ? !state : state));
		setCheckedState(updatedState);
		if (!updatedState[i]) {
			setSelectAll(false);
		}
	};

	const isSelectAllDisabled = () => {
		return productInfo.every((product) => isServiveActiveOrPending(product?.status));
	};

	const renderSelectAll = () => {
		const disabled = !activeCommitment || isSelectAllDisabled();
		return (
			<div className={classes?.selectAllWrapper}>
				<Checkbox
					className={disabled ? classes.disabledCheckbox : classes?.checkbox}
					color="primary"
					onChange={selectAllOnChange}
					name="select-all"
					checked={selectAll || disabled}
					id="select-all"
					aria-disabled={disabled}
				/>
				<div className={classes?.service}>
					<CustomLabel
						className={classes.serviceName}
						htmlFor="select-all"
						labelName={translate('mb.additional.benefits.selectall')}
					/>
				</div>
			</div>
		);
	};

	const derieveServiceStatusClass = (status: string) => {
		const serviceStatus = status?.toLowerCase();
		switch (serviceStatus) {
			case SERVICE_STATUS.ACTIVE:
				return SERVICE_STATUS.ACTIVE;
			case SERVICE_STATUS.PENDING:
			case SERVICE_STATUS.PENDING_ACTIVATION:
				return SERVICE_STATUS.PENDING;
			default:
				return status;
		}
	};

	const heading = newCustomer
		? translate('mb.additional.benefits.plan', selectedOption?.name || '')
		: translate('mb.additional.benefits.plan.activation', planName || '');

	const isMs365Disable = (partNumber: string, status: string) => {
		return MK_365_PART_NUMBERS.includes(partNumber) && !isServiceActive(status) && !enableMs365;
	};

	return (
		<>
			<ConditionalRender
				show={!activeCommitment && !isServiceLoading}
				onTrue={() => <Note text={aamaliPlusLabels.noCommitmentWarning} removeSpace={true} />}
			/>

			<Card
				className={classNames({
					[classes.cardWrapper]: true,
					[classes.disabledServices]: !activeCommitment,
				})}
			>
				<div className={classes.serviceHeading}>
					<HeadingLBold>{heading}</HeadingLBold>
				</div>
				<div className={classes.serviceDescription}>
					<BodyMBold>{aamaliPlusLabels.additionalBenHeading}</BodyMBold>
					<DescriptionM>{aamaliPlusLabels.additionalDesc}</DescriptionM>
				</div>
				<>
					{loading || isServiceLoading ? (
						<CircularProgress />
					) : (
						<div className={classes?.serviceComponentWrapper}>
							{renderSelectAll()}
							{productInfo.length > 0 &&
								productInfo.map((service: any, i: number) => {
									if (!service) return null;
									const checkboxState =
										MK_365_PART_NUMBERS.includes(service.partNumber) && !enableMs365
											? false
											: checkedState[i];
									const status = service?.status || '';
									const statusClass: string = derieveServiceStatusClass(service?.status);
									const disabled =
										isServiveActiveOrPending(status) ||
										(MK_365_PART_NUMBERS.includes(service.partNumber) && !enableMs365) ||
										!activeCommitment;

									return (
										<div key={service?.partNumber} className={classes.singleServiceWrapper}>
											<div className={classes?.serviceWrapper}>
												<div>
													<Checkbox
														id={service?.partNumber}
														checked={getCheckedStatus(!!checkboxState, status)}
														className={
															disabled ? classes.disabledCheckbox : classes?.checkbox
														}
														color="primary"
														disabled={disabled}
														onChange={() => handleSingleCheckbox(i)}
														name={service?.partNumber}
														aria-disabled={disabled}
														aria-label={'status' + ' ' + status}
													/>
													<CustomLabel
														className={classes.serviceName}
														htmlFor={service?.partNumber}
														labelName={service?.name}
													/>
												</div>

												{isServiveActiveOrPending(status) && (
													<Chip
														label={
															statusClass === SERVICE_STATUS.PENDING ||
															statusClass === SERVICE_STATUS.PENDING_ACTIVATION
																? translate('mb.additional.benefit.status.pending')
																: translate('mb.additional.benefit.status.active')
														}
														size="small"
														className={classes[statusClass]}
													/>
												)}
											</div>
											<>
												<ServiceInfo
													data={
														newCustomer
															? extractServiceData(service?.attributes)
															: service?.serviceDetails
													}
												/>
											</>

											<div
												className={classNames({
													[classes.externalLinks]: true,
													[classes.alignBetween]: isMs365Disable(
														service.partNumber,
														service.status
													),
												})}
											>
												{isMs365Disable(service.partNumber, service.status) && (
													<Note text={aamaliPlusLabels.hold365Banner} removeSpace={true} />
												)}
												<ConditionalRender
													show={
														service?.partNumber &&
														tutorialExtLink[service?.partNumber] &&
														statusClass === SERVICE_STATUS.ACTIVE
													}
													onTrue={() => (
														<Button
															color="primary-light"
															className={classes.viewTutorial}
															disabled={
																MK_365_PART_NUMBERS.includes(service.partNumber) &&
																!enableMs365
															}
															onClick={() => {
																window.open(
																	tutorialExtLink[service?.partNumber],
																	'newwindow'
																);
															}}
														>
															{aamaliPlusLabels.viewTutorial}
														</Button>
													)}
												/>
												<ConditionalRender
													show={
														service?.partNumber &&
														platformExtLink[service?.partNumber] &&
														statusClass === SERVICE_STATUS.ACTIVE
													}
													onTrue={() => (
														<Button
															color="primary-light"
															className={classes.viewTutorial}
															disabled={
																MK_365_PART_NUMBERS.includes(service.partNumber) &&
																!enableMs365
															}
															onClick={() => {
																window.open(
																	platformExtLink[service?.partNumber],
																	'newwindow'
																);
															}}
														>
															{aamaliPlusLabels.accessPlatform}
														</Button>
													)}
												/>
											</div>
										</div>
									);
								})}
						</div>
					)}
				</>
			</Card>
		</>
	);
};
